import { html, LitElement } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import { RootState, store } from '@nirvana/redux/store';
import { SearchableAccordionsViewWebComponent } from '../../../../../../ui/gzSearchableAccordionsView';
import { convertUiElementIdToSectionId, getPolicyPanelTreeItems } from '../../searchablePolicyPanel/utils/treeItems';
import { selectPoliciesState } from '../../../redux/slice';
import {
  getFullUpdateStatusNeeded,
  getPathToBeUpdatedWithStatus,
  getSectionStatus,
  getSectionStatuses,
  getPathUpdateNeeded,
  setIsStatusFullUpdateNeeded,
  setIsStatusPathUpdateNeeded,
  setPathStatuses,
} from '../../../redux/statusSlice/actions.status';
import {
  getSectionStatusFromPolicy,
  getStatusRendererForModule,
  getStatusRendererForSection,
} from '../../../utils/status';
import { PolicyPanelManager } from '../../../PolicyPanelManager';
import { ModuleId, Policy, SectionId, TreePath } from '@nirvana/shared/dtos/types/policies/common';
import { PolicyAppState, SectionStatus, SectionStatusType } from '../../../redux/types';
import { PolicyContext } from '../../../redux/navigationHeaderSlice/types.navigation';
import { translateFn } from '../../../../../../utils/translator.helper';
import { SearchableAccordionsPanelViewConfigs } from '../../../../../../ui/gzSearchableAccordionsPanel/types';

@customElement('gz-searchable-policy-panel-view')
export class GzSearchablePolicyPanelViewWebComponent extends connect(store)(LitElement) {
  @query('gz-searchable-accordions-view')
  private searchableAccordionsView: SearchableAccordionsViewWebComponent;

  private isInitialized = false;

  private searchablePanelViewConfigs: SearchableAccordionsPanelViewConfigs = {
    searchStringMinLength: 3,
    searchInputPlaceholder: translateFn('Search (min. 3 characters)'),
    panelWidth: 280,
  };

  /**
   * When store state changed, may need to update the status of the sections.
   * @param rootState
   */
  public stateChanged(rootState: RootState) {
    const policiesApp = selectPoliciesState(rootState);

    // when full update is needed, we need to update all the statuses
    const fullUpdateNeeded = getFullUpdateStatusNeeded(policiesApp);
    if (fullUpdateNeeded) {
      this.updateAllAccordionStatuses();
      return;
    }

    // when path update is needed, we need to update the status of the specific path
    this.updateAccordionStatusIfNeeded(policiesApp);
  }

  /**
   * Updates the status of all the sections in the policy panel.
   */
  private updateAllAccordionStatuses(): void {
    let idxModule, idxSection;
    const policy = PolicyPanelManager.getInstance().getPolicyForSaving();
    const sectionStatusesMap: { path: TreePath; status: SectionStatus }[] = [];

    // iterate over all the modules
    for (idxModule = 0; idxModule < this.searchableAccordionsView.accordions.length; idxModule++) {
      const sectionStatuses: SectionStatus[] = [];
      const moduleId = this.searchableAccordionsView.accordions[idxModule].id as ModuleId;

      // iterate over all the sections in the module
      for (
        idxSection = 0;
        idxSection < this.searchableAccordionsView.accordions[idxModule].children.length;
        idxSection++
      ) {
        const sectionId = convertUiElementIdToSectionId(
          this.searchableAccordionsView.accordions[idxModule].children[idxSection].id
        ) as SectionId;
        const sectionStatus = this.computeAndUpdateSectionStatus(moduleId, sectionId, idxModule, idxSection, policy);

        sectionStatuses.push(sectionStatus);
        sectionStatusesMap.push({ path: { moduleId: moduleId, sectionId: sectionId }, status: sectionStatus });
      }

      if (!sectionStatuses.length) {
        // if there are no sections in the module, then we need to update the module status as a section
        const sectionId = SectionId.NONE;
        const sectionStatus = this.computeAndUpdateSectionStatus(moduleId, sectionId, idxModule, idxSection, policy);

        sectionStatusesMap.push({ path: { moduleId: moduleId, sectionId: sectionId }, status: sectionStatus });
      } else {
        // if there are sections in the module, then we need to update the module status as a module
        this.searchableAccordionsView.accordions[idxModule].renderer = item =>
          getStatusRendererForModule(sectionStatuses);
      }
    }

    this.searchableAccordionsView.requestUpdate();
    store.dispatch(setIsStatusFullUpdateNeeded(false));
    store.dispatch(setPathStatuses(sectionStatusesMap));

    this.isInitialized = true;
  }

  /**
   * Computes and updates the status of a section in policy panel.
   * @param moduleId
   * @param sectionId
   * @param idxModule
   * @param idxSection
   * @param policy
   */
  computeAndUpdateSectionStatus(
    moduleId: ModuleId,
    sectionId: SectionId,
    idxModule: number,
    idxSection: number,
    policy: Policy | null
  ): SectionStatus {
    let sectionStatus: SectionStatus;
    try {
      sectionStatus = getSectionStatusFromPolicy({ moduleId: moduleId, sectionId: sectionId }, policy);
    } catch (e) {
      sectionStatus = { type: SectionStatusType.NONE };
    }

    if (sectionId === SectionId.NONE) {
      this.searchableAccordionsView.accordions[idxModule].renderer = item => getStatusRendererForSection(sectionStatus);
    } else {
      this.searchableAccordionsView.accordions[idxModule].children[idxSection].renderer = item =>
        getStatusRendererForSection(sectionStatus);
    }

    return sectionStatus;
  }

  /**
   * Updates the status of the accordion if needed.
   * @param policiesApp
   */
  private updateAccordionStatusIfNeeded(policiesApp: PolicyAppState) {
    if (!this.isInitialized) {
      return;
    }

    // check if any path needs to be updated
    const isUpdateStatusNeeded = getPathUpdateNeeded(policiesApp);
    if (!isUpdateStatusNeeded) {
      return;
    }

    // get the path to be updated
    const pathToBeChanged = getPathToBeUpdatedWithStatus(policiesApp);
    if (!pathToBeChanged) {
      store.dispatch(setIsStatusPathUpdateNeeded(false));
      return;
    }

    // find module index
    const idxModule = this.searchableAccordionsView.accordions.findIndex(item => item.id === pathToBeChanged.moduleId);
    if (idxModule === -1) {
      store.dispatch(setIsStatusPathUpdateNeeded(false));
      return;
    }

    // find section index
    const idxSection = this.searchableAccordionsView.accordions[idxModule].children.findIndex(
      item => convertUiElementIdToSectionId(item.id) === pathToBeChanged.sectionId
    );
    const hasChildren = this.searchableAccordionsView.accordions[idxModule].children.length > 0;
    if (idxSection === -1 && hasChildren) {
      store.dispatch(setIsStatusPathUpdateNeeded(false));
      return;
    }

    // update the status of the section and module
    const sectionStatusRenderer = getStatusRendererForSection(getSectionStatus(policiesApp, pathToBeChanged));
    let moduleStatusRenderer;

    if (hasChildren) {
      moduleStatusRenderer = getStatusRendererForModule(getSectionStatuses(policiesApp, pathToBeChanged.moduleId));
      this.searchableAccordionsView.accordions[idxModule].children[idxSection].renderer = item => sectionStatusRenderer;
    } else {
      moduleStatusRenderer = sectionStatusRenderer;
    }
    this.searchableAccordionsView.accordions[idxModule].renderer = item => moduleStatusRenderer;

    this.searchableAccordionsView.requestUpdate();
    store.dispatch(setIsStatusPathUpdateNeeded(false));
  }

  public setDataView(data: { routingPath: string; policy: Policy; phantom: boolean; context: PolicyContext }) {
    this.searchableAccordionsView.accordions = getPolicyPanelTreeItems(data.phantom);

    PolicyPanelManager.getInstance().setPolicy(data.policy);
    PolicyPanelManager.getInstance().setPolicyInfoForNavigation(data.policy.name, data.context);

    PolicyPanelManager.getInstance().setInheritanceMask(data.policy.inheritance || []);
    PolicyPanelManager.getInstance().applyFeatureRestrictions();
    PolicyPanelManager.getInstance().applySubscriptionRestrictions();
    PolicyPanelManager.getInstance().applyEditingAccessRestrictions(data.phantom);

    store.dispatch(setIsStatusFullUpdateNeeded(true));
  }

  /**
   * Renders the component.
   */
  protected render() {
    return html`
      <gz-searchable-accordions-view
        .searchablePanelViewConfigs="${this.searchablePanelViewConfigs}"
      ></gz-searchable-accordions-view>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    searchablePolicyPanelView: GzSearchablePolicyPanelViewWebComponent;
  }
}
